import { WorkItem } from '../api/workItems';
import { DecisionGuide, InvoiceData, ScamRisk } from '../components/DrawerFacetTypes';
import { getWorkItemSourceInfo } from './workItemDisplay';

export const NEXT_STEP_REJECT = new Set([
    'review manually', 'n/a', 'none', 'unknown', '-',
    'open this email to decide the next step',
]);

export type NextStepSource =
    | 'scam_risk'
    | 'decision_guide'
    | 'ai_enrichment'
    | 'invoice'
    | 'due_date'
    | 'event'
    | 'email'
    | 'user';

export type NextStepCtaKind = 'open_email' | 'open_document' | 'copy_template' | 'open_url';

export interface NextStepCta {
    label: string;
    kind: NextStepCtaKind;
    payload?: string;
}

/** Where the item itself came from, for the provenance word. */
export type NextStepOrigin = 'email' | 'document' | 'note';

export interface NextStep {
    text: string;
    source: NextStepSource;
    cta?: NextStepCta;
    origin?: NextStepOrigin;
}

export const NEXT_STEP_SOURCE_LABEL: Record<NextStepSource, string> = {
    scam_risk: 'from safety check',
    decision_guide: 'from document guide',
    ai_enrichment: 'from the email',
    invoice: 'from the invoice',
    due_date: 'from the due date',
    event: 'from the date',
    email: 'from the email',
    user: 'from your note',
};

const ORIGIN_LABEL: Record<NextStepOrigin, string> = {
    email: 'from the email',
    document: 'from the document',
    note: 'from your note',
};

/** Provenance word for the card. Text read from the item names the item's own source. */
export function nextStepSourceLabel(step: NextStep): string {
    if ((step.source === 'email' || step.source === 'ai_enrichment') && step.origin) {
        return ORIGIN_LABEL[step.origin];
    }
    return NEXT_STEP_SOURCE_LABEL[step.source];
}

export type StakeTone = 'critical' | 'urgent' | 'neutral' | 'money';

export interface DrawerStake {
    id: string;
    label: string;
    tone: StakeTone;
}

export interface DeriveNextStepInput {
    item: WorkItem | null;
    scamRisk: ScamRisk | null;
    decisionGuide: DecisionGuide | null;
    invoiceData: InvoiceData | null;
    hasLinkedEmail: boolean;
    /** A linked id alone does not prove that a document can be opened. */
    documentAvailable?: boolean;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function parseLocalDrawerDate(value: string | null | undefined): Date | null {
    if (!value) return null;
    const prefix = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (prefix) {
        const year = Number(prefix[1]);
        const month = Number(prefix[2]) - 1;
        const day = Number(prefix[3]);
        const parsed = new Date(year, month, day);
        return parsed.getFullYear() === year && parsed.getMonth() === month && parsed.getDate() === day
            ? parsed : null;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfLocalDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function daysFromToday(value: string | null | undefined, now: Date = new Date()): number | null {
    const parsed = parseLocalDrawerDate(value);
    if (!parsed) return null;
    return Math.round((startOfLocalDay(parsed).getTime() - startOfLocalDay(now).getTime()) / MS_PER_DAY);
}

/** The drawer's one date format: "Oct 14", with the year only when it is not this year. */
export function formatHumanDrawerDate(value: string | null | undefined, now: Date = new Date()): string | null {
    const parsed = parseLocalDrawerDate(value);
    if (!parsed) return null;
    return parsed.getFullYear() === now.getFullYear()
        ? parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function normalizeNextStepText(raw: unknown): string | null {
    if (typeof raw !== 'string') return null;
    const collapsed = raw.replace(/\s+/g, ' ').trim();
    if (!collapsed) return null;
    if (NEXT_STEP_REJECT.has(collapsed.replace(/[.]+$/u, '').toLowerCase())) return null;
    const capped = collapsed.length > 160 ? collapsed.slice(0, 160).trimEnd() : collapsed;
    return capped.replace(/[.]+$/u, '');
}

function templateFromGuide(guide: DecisionGuide | null): string | null {
    if (!guide?.templates) return null;
    const call = typeof guide.templates.call_script === 'string' ? guide.templates.call_script.trim() : '';
    if (call) return call;
    const letter = typeof guide.templates.letter_template === 'string' ? guide.templates.letter_template.trim() : '';
    return letter || null;
}

function withCta(
    text: string,
    source: NextStepSource,
    input: DeriveNextStepInput,
    extra?: NextStepCta,
): NextStep {
    if (extra) return { text, source, cta: extra };
    if (input.hasLinkedEmail) {
        return { text, source, cta: { label: 'Open in Gmail', kind: 'open_email' } };
    }
    if (input.item?.document_id && input.documentAvailable === true) {
        return { text, source, cta: { label: 'Open document', kind: 'open_document' } };
    }
    return { text, source };
}

const ACTION_VERB =
    'reply|confirm|rsvp|pay|call|sign|send|share|prepare|accept|decline|submit|respond|attend|complete|return|book|follow[\\s-]?up|schedule';

function isActionableAttention(item: WorkItem): boolean {
    if (item.needs_action !== true) return false;
    if (item.attention === 'watch' || item.attention === 'ignore') return false;
    const state = (item.state || '').toUpperCase();
    return state !== 'DONE' && state !== 'ARCHIVED';
}

function firstSentence(raw: string | null | undefined): string | null {
    if (!raw) return null;
    const line = raw.split(/\n/)[0]?.trim() || '';
    const sentence = line.split(/(?<=[.!?])\s+/)[0]?.trim() || '';
    return normalizeNextStepText(sentence);
}

/** True when the sentence is asking the user to act, not reporting status. */
function looksLikeRequest(text: string): boolean {
    if (/\b(do not|don't|does not|doesn't|no need to|not (need|required) to|never)\b/i.test(text)) {
        return false;
    }
    if (/\b(we|they|i|recruiter|someone)\s+will\b/i.test(text)) {
        return false;
    }
    // Status / login first lines often start with an action verb used as a noun.
    // Keep these on the constructions they name: "Sign in to …", "Log in to …",
    // "Call scheduled …" — not "Please reply with your login details" or
    // "Please sign in the box" or "Please confirm the interview scheduled
    // for Tuesday".
    if (/^(?:please\s+|kindly\s+)?(?:log\s*in|login|sign\s*in)(?:\s+to\b|$)/i.test(text)) {
        return false;
    }
    if (/^(?:call|interview|meeting|appointment)\s+scheduled\b/i.test(text)) {
        return false;
    }
    const request = new RegExp(
        `^(please\\s+|kindly\\s+)?(${ACTION_VERB})\\b` +
            `|\\b(please|kindly)\\s+(${ACTION_VERB})\\b` +
            `|\\b(can you|could you|would you)\\s+(${ACTION_VERB})\\b` +
            `|\\byou\\s+(need to|must|should)\\s+(${ACTION_VERB})\\b`,
        'i',
    );
    return request.test(text);
}

function actionableLine(raw: string | null | undefined): string | null {
    const text = firstSentence(raw);
    if (!text || !looksLikeRequest(text)) return null;
    return text;
}

/** Only explicit requests support a recommendation. Categories and dates are facts. */
function deriveFromClassifiedFields(item: WorkItem): { text: string; source: NextStepSource } | null {
    const fromSummary = actionableLine(item.summary);
    if (fromSummary) return { text: fromSummary, source: 'email' };

    const fromTitle = actionableLine(item.title);
    if (fromTitle) return { text: fromTitle, source: 'email' };

    return null;
}

function sameSentence(a: unknown, b: unknown): boolean {
    const left = normalizeNextStepText(a);
    const right = normalizeNextStepText(b);
    return Boolean(left && right && left.toLowerCase() === right.toLowerCase());
}

function itemOrigin(item: WorkItem): NextStepOrigin | undefined {
    const { sourceType } = getWorkItemSourceInfo(item);
    if (sourceType === 'email') return 'email';
    if (sourceType === 'document') return 'document';
    if (sourceType === 'manual') return 'note';
    return undefined;
}

/**
 * One trustworthy next action. First match wins. Returns null when nothing
 * is solid enough to show — never invents "review manually" — and when the
 * sentence would only repeat what is already on screen.
 */
export function deriveNextStep(input: DeriveNextStepInput): NextStep | null {
    const step = deriveFirstNextStep(input);
    if (!step || !input.item) return step;
    if (step.source !== 'scam_risk') {
        // A sentence that only repeats the title is a second view of the same fact.
        if (sameSentence(step.text, input.item.title)) return null;
        // The medium/high alert already shows this safe first step above the verbs.
        const alertStep = input.scamRisk && input.scamRisk.level !== 'low'
            ? input.scamRisk.recommended_first_step
            : null;
        if (sameSentence(step.text, alertStep)) return null;
    }
    const origin = itemOrigin(input.item);
    return origin ? { ...step, origin } : step;
}

function deriveFirstNextStep(input: DeriveNextStepInput): NextStep | null {
    const { item, scamRisk, decisionGuide } = input;
    if (!item) return null;

    if (scamRisk?.level === 'high') {
        // High risk stops here. Product decision (2026-09-04): never offer a reply /
        // Gmail button on a high-risk item, so the lower branches must not run.
        // Safety copy is not gated on open attention — watch/done still need it.
        const text = normalizeNextStepText(scamRisk.recommended_first_step);
        return text ? { text, source: 'scam_risk' } : null;
    }

    const state = (item.state || '').toUpperCase();
    if (state === 'DONE' || state === 'ARCHIVED') return null;

    const guideStep = normalizeNextStepText(decisionGuide?.safe_first_step);
    // Document-guide copy is inferred. Watch/ignore still keep user notes and
    // high-risk safety, not this AI instruction.
    if (guideStep && isActionableAttention(item)) {
        const template = templateFromGuide(decisionGuide);
        return withCta(
            guideStep,
            'decision_guide',
            input,
            template ? { label: 'Copy response', kind: 'copy_template', payload: template } : undefined,
        );
    }

    const meta = (item.meta || {}) as Record<string, unknown>;
    const aiStep = normalizeNextStepText(meta.next_step);
    if (aiStep) {
        const source: NextStepSource = meta.next_step_source === 'user' ? 'user' : 'ai_enrichment';
        // User notes stay visible on watch/ignore. Inferred AI copy does not.
        if (source === 'user' || isActionableAttention(item)) {
            return withCta(aiStep, source, input);
        }
    }

    // Inferred summary/title requests only on open attention.
    if (!isActionableAttention(item)) return null;

    const classified = deriveFromClassifiedFields(item);
    if (classified) return withCta(classified.text, classified.source, input);

    return null;
}

export function deriveDrawerStakes(input: {
    item: WorkItem | null;
    invoiceData: InvoiceData | null;
}): DrawerStake[] {
    const { item } = input;
    if (!item || !isActionableAttention(item)) return [];
    const stakes: DrawerStake[] = [];

    const dueDelta = daysFromToday(item.due_date);
    if (dueDelta !== null) {
        if (dueDelta < 0) stakes.push({ id: 'due', label: 'Overdue', tone: 'critical' });
        else if (dueDelta === 0) stakes.push({ id: 'due', label: 'Due today', tone: 'urgent' });
        else if (dueDelta === 1) stakes.push({ id: 'due', label: 'Due tomorrow', tone: 'urgent' });
        else if (dueDelta <= 7) stakes.push({ id: 'due', label: `Due in ${dueDelta} days`, tone: 'urgent' });
    }

    const eventDate = formatHumanDrawerDate(item.event_date);
    const eventType = (item.event_type || '').trim();
    if (eventType && eventDate) {
        const pretty = eventType.charAt(0).toUpperCase() + eventType.slice(1);
        stakes.push({ id: 'event', label: `${pretty} · ${eventDate}`, tone: 'neutral' });
    } else if (eventType) {
        stakes.push({
            id: 'event',
            label: eventType.charAt(0).toUpperCase() + eventType.slice(1),
            tone: 'neutral',
        });
    }

    return stakes.slice(0, 3);
}
